import { Spinner, useTranslation } from '@hbs-us/ui';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { ApiError } from '../../lib/apiFetch.js';
import { QcChecklistView } from './QcChecklistView.js';
import { QcListView } from './QcListView.js';
import { useQcChecklistQuery, useQcChecklistsQuery } from './useQc.js';

/**
 * Route wrappers for quality control.
 *
 * The `quality_control` tier gate lives on the api, which answers `feature_required` with an
 * upgrade URL for a tenant below Pro. That is surfaced as its own message rather than the generic
 * error, because "upgrade to use this" and "something went wrong" are different things and only one
 * of them is actionable.
 */

function Loading() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full items-center justify-center bg-background">
      <Spinner label={t('common:status.loading')} />
    </div>
  );
}

function Failure({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const code = error instanceof ApiError ? error.message : 'generic';
  const upgrade = code === 'feature_required';

  return (
    <section
      role="alert"
      aria-live="assertive"
      className="flex h-full flex-col items-center justify-center gap-3 bg-background p-12 text-center"
    >
      <h1 className="font-display font-semibold text-2xl text-foreground">
        {t(upgrade ? 'qc:gate.heading' : 'qc:errors.heading')}
      </h1>
      <p className="max-w-sm text-muted-foreground text-sm">
        {t(upgrade ? 'qc:gate.body' : `qc:errors.${code}`, { defaultValue: t('qc:errors.generic') })}
      </p>
    </section>
  );
}

export function QcListRoute() {
  const [mineOnly, setMineOnly] = useState(false);
  // Archived rows are out until asked for, matching the api's default. Local state rather than a
  // search param, like `mine` beside it — this is a view toggle, not a place worth linking to.
  const [showArchived, setShowArchived] = useState(false);
  const query = useQcChecklistsQuery({ mine: mineOnly, showArchived });

  if (query.isPending) return <Loading />;
  if (query.isError) return <Failure error={query.error} />;

  return (
    <QcListView
      checklists={query.data?.checklists ?? []}
      mineOnly={mineOnly}
      onToggleMine={setMineOnly}
      showArchived={showArchived}
      onToggleArchived={setShowArchived}
    />
  );
}

export function QcChecklistRoute() {
  const { id } = useParams({ from: '/qc/$id' });
  const checklistId = Number(id);
  const query = useQcChecklistQuery(Number.isFinite(checklistId) ? checklistId : null);

  if (query.isPending) return <Loading />;
  if (query.isError) return <Failure error={query.error} />;

  return <QcChecklistView data={query.data ?? {}} />;
}
